// Role-aware copy for the final step of the personalised invitee welcome flow.
// Key on (teamType, role) — the BE returns Title-Case role strings, used here
// directly to avoid lossy snake_case round-tripping.
//
// Each entry is intentionally short: the welcome is a *direction*, not a
// tutorial. Two-sentence body + one primary CTA + 2-3 micro-tips.

export type TeamType = "DealershipGroup" | "Dealership" | "Bodyshop"

export type TipIcon = "estimate" | "team" | "settings" | "dashboard" | "report" | "vehicle"

export interface RoleTourEntry {
  headline: string
  body: string
  primaryCTA: { label: string; to: string }
  secondaryCTA?: { label: string; to: string }
  tips?: { icon: TipIcon; text: string }[]
}

const BODYSHOP_GM: RoleTourEntry = {
  headline: "You're running the shop.",
  body: "Estimates, fees, team, and the customer pipeline all roll up to you. Here's the room.",
  primaryCTA: { label: "Open the dashboard", to: "/dashboard" },
  secondaryCTA: { label: "Review fees & rates", to: "/settings" },
  tips: [
    { icon: "dashboard", text: "Today's queue and revenue land on the dashboard." },
    { icon: "team", text: "Invite estimators from Settings · Team." },
    { icon: "settings", text: "Labor rates and fees live under Settings." },
  ],
}

const BODYSHOP_ESTIMATOR: RoleTourEntry = {
  headline: "Estimates are your craft.",
  body: "Snap photos, mark damage, send a clean estimate. Your queue is one click away.",
  primaryCTA: { label: "Write your first estimate", to: "/estimates/full?return=/dashboard" },
  secondaryCTA: { label: "See open estimates", to: "/estimates" },
  tips: [
    { icon: "vehicle", text: "Quick estimate for walk-ins, full estimate for sit-downs." },
    { icon: "estimate", text: "Drafts auto-save — leave and come back any time." },
    { icon: "report", text: "Send a PDF straight to the customer when you're done." },
  ],
}

const DEALERSHIP_GM: RoleTourEntry = {
  headline: "You run the dealership.",
  body: "Service, sales, and the connected body shop all surface here. Start with the dashboard.",
  primaryCTA: { label: "Open the dashboard", to: "/dashboard" },
  secondaryCTA: { label: "Manage the team", to: "/settings" },
  tips: [
    { icon: "dashboard", text: "Today's traffic and pipeline are on the dashboard." },
    { icon: "team", text: "Invite advisors and managers from Settings · Team." },
    { icon: "settings", text: "Connect or change your body shop in Settings." },
  ],
}

const DEALERSHIP_SERVICE_MANAGER: RoleTourEntry = {
  headline: "You oversee service.",
  body: "Triage what advisors send up, keep the queue moving, hand the heavy stuff to the body shop.",
  primaryCTA: { label: "Open the dashboard", to: "/dashboard" },
  secondaryCTA: { label: "See open estimates", to: "/estimates" },
  tips: [
    { icon: "estimate", text: "Estimates flagged for review show up first." },
    { icon: "team", text: "Your advisors live under Settings · Team." },
    { icon: "vehicle", text: "Send to the body shop in one click from the estimate." },
  ],
}

const DEALERSHIP_SERVICE_ADVISOR: RoleTourEntry = {
  headline: "You're the eyes on the floor.",
  body: "Walk-ins, intake, and the first cut on every job. The quick estimate is built for speed.",
  primaryCTA: { label: "Start a quick estimate", to: "/estimates/quick?return=/dashboard" },
  secondaryCTA: { label: "See your estimates", to: "/estimates" },
  tips: [
    { icon: "vehicle", text: "Photo-first capture — VIN scanner does the rest." },
    { icon: "estimate", text: "Hand off to the body shop when the damage is real." },
    { icon: "report", text: "Customer gets a polished PDF, not a scribbled quote." },
  ],
}

const DEALERSHIP_SALES_MANAGER: RoleTourEntry = {
  headline: "You drive the floor.",
  body: "Pipeline, quotas, and the team's daily numbers — all one click away.",
  primaryCTA: { label: "Open the dashboard", to: "/dashboard" },
  secondaryCTA: { label: "Manage the team", to: "/settings" },
  tips: [
    { icon: "dashboard", text: "Pipeline and pacing live on the dashboard." },
    { icon: "team", text: "Invite reps from Settings · Team." },
  ],
}

const DEALERSHIP_SALES_REP: RoleTourEntry = {
  headline: "Move the metal.",
  body: "Your leads, your appointments, your follow-ups. Everything else is noise.",
  primaryCTA: { label: "Open the dashboard", to: "/dashboard" },
  tips: [
    { icon: "dashboard", text: "Today's appointments are on the dashboard." },
    { icon: "vehicle", text: "VIN scan on every walk-in — no manual entry." },
  ],
}

const GROUP_DIRECTOR: RoleTourEntry = {
  headline: "You see across every store.",
  body: "Group view rolls up traffic, pipeline, and revenue from each location. Drill into a store any time.",
  primaryCTA: { label: "Open the group view", to: "/dashboard" },
  secondaryCTA: { label: "Manage locations", to: "/settings" },
  tips: [
    { icon: "dashboard", text: "Switch between group view and a single store from the dashboard." },
    { icon: "team", text: "Add dealerships and body shops from Settings." },
    { icon: "report", text: "Group reports compare locations head-to-head." },
  ],
}

const GROUP_GM: RoleTourEntry = {
  headline: "You run the group.",
  body: "Same view as a director, with day-to-day controls. Locations, teams, and KPIs are all here.",
  primaryCTA: { label: "Open the group view", to: "/dashboard" },
  secondaryCTA: { label: "Manage locations", to: "/settings" },
  tips: [
    { icon: "dashboard", text: "Group dashboard rolls up every store." },
    { icon: "team", text: "Add or invite from Settings · Team." },
  ],
}

// Two-level lookup: teamType → role (Title Case) → entry. Falls back to a
// generic team-type entry if a specific role isn't mapped (defensive: the BE
// could add a role we haven't authored copy for).
const REGISTRY: Record<TeamType, Record<string, RoleTourEntry>> = {
  Bodyshop: {
    "General Manager": BODYSHOP_GM,
    Estimator: BODYSHOP_ESTIMATOR,
  },
  Dealership: {
    "General Manager": DEALERSHIP_GM,
    "Service Manager": DEALERSHIP_SERVICE_MANAGER,
    "Service Advisor": DEALERSHIP_SERVICE_ADVISOR,
    "Sales Manager": DEALERSHIP_SALES_MANAGER,
    "Sales Representative": DEALERSHIP_SALES_REP,
  },
  DealershipGroup: {
    Director: GROUP_DIRECTOR,
    "General Manager": GROUP_GM,
  },
}

const FALLBACK_BY_TYPE: Record<TeamType, RoleTourEntry> = {
  Bodyshop: {
    headline: "Welcome to the shop.",
    body: "Your team has set things up. Open the dashboard to see what's on today.",
    primaryCTA: { label: "Open the dashboard", to: "/dashboard" },
  },
  Dealership: {
    headline: "Welcome to the dealership.",
    body: "Your team has set things up. Open the dashboard to get started.",
    primaryCTA: { label: "Open the dashboard", to: "/dashboard" },
  },
  DealershipGroup: {
    headline: "Welcome to the group.",
    body: "Your team has set things up. Open the dashboard to see across stores.",
    primaryCTA: { label: "Open the dashboard", to: "/dashboard" },
  },
}

export function getRoleTourEntry(teamType: string, role: string): RoleTourEntry {
  const tt = teamType as TeamType
  const byType = REGISTRY[tt]
  if (byType && byType[role]) return byType[role]
  return FALLBACK_BY_TYPE[tt] ?? FALLBACK_BY_TYPE.Dealership
}
